import RobotOnMountDetector from 'common/src/vtj/test/RobotOnMountDetector'
import { GlobalStyle as CommonGlobalStyle } from 'common-ui-components/src/vtj/theme/global'
import UnsupportedBrowserBanner from 'todistukset-ui/src/vtj/components/unsupported-browser-banner/UnsupportedBrowserBanner.react'
import { urlWithoutLang } from 'common/src/vtj/browser/localization'
import { setDocumentTitle } from 'common/src/vtj/browser/page-title'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import _ from 'lodash'
import { observer } from 'mobx-react'
import React, { useEffect } from 'react'
import { I18nextProvider } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { TodistuksetUi } from 'todistukset-ui/src/vtj/common-types'
import AppContainer from 'todistukset-ui/src/vtj/components/app-container/AppContainer.react'
import { GlobalStyle } from 'todistukset-ui/src/vtj/components/app/App.sc'
import Dialog from 'todistukset-ui/src/vtj/components/dialog/Dialog.react'
import Footer from 'todistukset-ui/src/vtj/components/footer/Footer.react'
import Header from 'todistukset-ui/src/vtj/components/header/Header.react'
import {
  getPage,
  parseComponentSelectionFromPath,
} from 'todistukset-ui/src/vtj/components/page-router/todistukset-router.util'
import TodistuksetRouter from 'todistukset-ui/src/vtj/components/page-router/TodistuksetRouter.react'
import TodistusThemeProvider from 'todistukset-ui/src/vtj/components/theme/TodistusThemeProvider.react'
import i18next from 'todistukset-ui/src/vtj/i18next'
import publicSettings from 'todistukset-ui/src/vtj/public-settings'
import { selectUiLanguage } from 'todistukset-ui/src/vtj/stores/actions/common-actions'
import { startSessionTimer } from 'todistukset-ui/src/vtj/stores/actions/session-timer-actions'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { updateFormPersonsAsiointikieli } from 'todistukset-ui/src/vtj/stores/actions/get-form-actions'

const getUnsupportedBrowserBanner = (store: TodistuksetUi.Store) => {
  const { browserUpdateRecommendations, lang } = store.common
  return (
    <UnsupportedBrowserBanner
      key="unsupportedBrowserBanner"
      browserUpdateRecommendations={browserUpdateRecommendations}
      lang={lang}
    />
  )
}

const getHeaderChildren = ({ store }: { store: TodistuksetUi.Store }) => {
  return _.compact([getUnsupportedBrowserBanner(store)])
}

const App: React.FC = () => {
  const store = useStore()
  const location = useLocation()
  const navigate = useNavigate()
  const settings = publicSettings.getPublicSettings()
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    startSessionTimer({ settings })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { user, lang } = store.common
  const { page, step, tab } = parseComponentSelectionFromPath(location.pathname)
  const { visiblePage } = getPage({ page, tab })

  const headerProps = {
    lang,
    user,
    selectLanguage: (language: LanguageCode) => {
      selectUiLanguage(language, i18next)
      updateFormPersonsAsiointikieli(language)
      setDocumentTitle(
        visiblePage.isStep
          ? visiblePage.getTitle(i18next.t, page ?? '', step ?? '')
          : visiblePage.getTitle(language)
      )
      if (location.pathname.indexOf('/') === 0) {
        navigate(
          `/${language}${urlWithoutLang(location.pathname, location.search)}`
        )
      }
    },
    loginHref: `/auth/suomifi/login?RelayState=/${lang}&lang=${lang}`,
    logoutHref: `/auth/suomifi/logout?RelayState=/${lang}`,
    children: getHeaderChildren({ store }),
    testAuthenticatorHref: settings.get('vaakaTestAuthenticator')
      ? `/auth/suomifi/login?isTestAuthenticator=1&RelayState=/${lang}&lang=${lang}`
      : '',
    isErrorPage: store.errors.length > 0,
  } satisfies React.ComponentProps<typeof Header>

  return (
    <>
      <TodistusThemeProvider>
        <I18nextProvider i18n={i18next}>
          <AppContainer lang={lang}>
            <Header {...headerProps} />
            <TodistuksetRouter store={store} />
            <Dialog />
            <Footer lang={lang} feedbackUrl={settings.get('feedbackFormUrl')} />
          </AppContainer>
        </I18nextProvider>
        <CommonGlobalStyle />
        <GlobalStyle />
      </TodistusThemeProvider>
      <RobotOnMountDetector />
    </>
  )
}

export default observer(App)
